/**
 * Error message utilities for user-friendly error handling
 */

import { APIServiceError } from '../services/api/APIService';

/**
 * Error message configuration
 */
export interface ErrorMessageConfig {
  /** User-friendly error message */
  message: string;
  /** Whether the operation can be retried */
  canRetry: boolean;
  /** Suggested action for the user */
  action?: string;
  /** Icon/emoji to display with the message */
  icon?: string;
}

/**
 * Map error codes to user-friendly messages
 */
const ERROR_MESSAGE_MAP: Record<string, ErrorMessageConfig> = {
  // Authentication errors
  NOT_AUTHENTICATED: {
    message: 'You need to be logged in to perform this action.',
    canRetry: false,
    action: 'Please log in and try again.',
    icon: '🔒',
  },
  UNAUTHORIZED_ACCESS: {
    message: 'You do not have permission to access this composition.',
    canRetry: false,
    action: 'This composition may belong to another user.',
    icon: '⛔',
  },

  // Composition errors
  COMPOSITION_NOT_FOUND: {
    message: 'Composition not found.',
    canRetry: false,
    action: 'It may have been deleted or moved.',
    icon: '❌',
  },
  COMPOSITION_LIMIT_REACHED: {
    message: 'You have reached your composition storage limit.',
    canRetry: false,
    action: 'Delete old compositions or upgrade your plan.',
    icon: '📦',
  },
  COMPOSITION_TOO_LARGE: {
    message: 'Your composition is too large to save.',
    canRetry: false,
    action: 'Try reducing the number of instruments or notes.',
    icon: '📏',
  },
  INVALID_COMPOSITION_DATA: {
    message: 'The composition data is invalid.',
    canRetry: false,
    action: 'Please check your composition and try again.',
    icon: '⚠️',
  },

  // Network and server errors
  NETWORK_ERROR: {
    message: 'Network connection error.',
    canRetry: true,
    action: 'Please check your internet connection and try again.',
    icon: '🌐',
  },
  S3_UPLOAD_FAILED: {
    message: 'Failed to upload composition to storage.',
    canRetry: true,
    action: 'Please try again in a moment.',
    icon: '☁️',
  },
  S3_DOWNLOAD_FAILED: {
    message: 'Failed to download composition from storage.',
    canRetry: true,
    action: 'Please try again in a moment.',
    icon: '☁️',
  },
  DYNAMODB_ERROR: {
    message: 'Database error occurred.',
    canRetry: true,
    action: 'Please try again in a moment.',
    icon: '💾',
  },

  // Generic errors
  INVALID_RESPONSE: {
    message: 'Received invalid response from server.',
    canRetry: true,
    action: 'Please try again.',
    icon: '⚠️',
  },
  UNKNOWN_ERROR: {
    message: 'An unexpected error occurred.',
    canRetry: true,
    action: 'Please try again.',
    icon: '❌',
  },
};

/**
 * Get user-friendly error message configuration from an error
 *
 * @param error - Error object
 * @returns Error message configuration
 */
export function getErrorMessage(error: unknown): ErrorMessageConfig {
  // Default fallback
  const defaultError: ErrorMessageConfig = {
    message: 'An unexpected error occurred.',
    canRetry: true,
    action: 'Please try again.',
    icon: '❌',
  };

  // Handle APIServiceError
  if (error instanceof APIServiceError) {
    // Check if we have a specific message for this error code
    if (error.code && error.code in ERROR_MESSAGE_MAP) {
      const config = ERROR_MESSAGE_MAP[error.code];
      if (config) {
        return config;
      }
    }

    // Check status code for common HTTP errors
    if (error.status === 401 && 'NOT_AUTHENTICATED' in ERROR_MESSAGE_MAP) {
      return ERROR_MESSAGE_MAP['NOT_AUTHENTICATED'];
    }
    if (error.status === 403 && 'UNAUTHORIZED_ACCESS' in ERROR_MESSAGE_MAP) {
      return ERROR_MESSAGE_MAP['UNAUTHORIZED_ACCESS'];
    }
    if (error.status === 404 && 'COMPOSITION_NOT_FOUND' in ERROR_MESSAGE_MAP) {
      return ERROR_MESSAGE_MAP['COMPOSITION_NOT_FOUND'];
    }
    if (error.status === 413 && 'COMPOSITION_TOO_LARGE' in ERROR_MESSAGE_MAP) {
      return ERROR_MESSAGE_MAP['COMPOSITION_TOO_LARGE'];
    }
    if (error.status && error.status >= 500) {
      return {
        message: 'Server error occurred.',
        canRetry: true,
        action: 'Please try again in a moment.',
        icon: '🔧',
      };
    }

    // Use the error message from the API if available
    return {
      message: error.message,
      canRetry: error.status ? error.status >= 500 : true,
      action: 'Please try again.',
      icon: '❌',
    };
  }

  // Handle TypeError (network errors)
  if (error instanceof TypeError && 'NETWORK_ERROR' in ERROR_MESSAGE_MAP) {
    return ERROR_MESSAGE_MAP['NETWORK_ERROR'];
  }

  // Handle generic Error
  if (error instanceof Error) {
    return {
      message: error.message,
      canRetry: true,
      action: 'Please try again.',
      icon: '❌',
    };
  }

  // Fallback for unknown error types
  return 'UNKNOWN_ERROR' in ERROR_MESSAGE_MAP ? ERROR_MESSAGE_MAP['UNKNOWN_ERROR'] : defaultError;
}

/**
 * Format error message for display
 *
 * @param error - Error object
 * @param includeAction - Whether to include the suggested action
 * @returns Formatted error message string
 */
export function formatErrorMessage(error: unknown, includeAction = true): string {
  const config = getErrorMessage(error);
  const parts = [config.icon, config.message];

  if (includeAction && config.action) {
    parts.push(config.action);
  }

  return parts.filter(Boolean).join(' ');
}

/**
 * Check if an error is retryable
 *
 * @param error - Error object
 * @returns True if the operation can be retried
 */
export function isRetryableError(error: unknown): boolean {
  const config = getErrorMessage(error);
  return config.canRetry;
}

/**
 * Get operation-specific error messages
 */
export const OPERATION_ERROR_MESSAGES = {
  save: {
    generic: 'Failed to save composition.',
    success: '✅ Composition saved successfully!',
  },
  load: {
    generic: 'Failed to load composition.',
    success: '✅ Composition loaded!',
  },
  delete: {
    generic: 'Failed to delete composition.',
    success: '✅ Composition deleted',
  },
  rename: {
    generic: 'Failed to rename composition.',
    success: '✅ Composition renamed',
  },
  duplicate: {
    generic: 'Failed to duplicate composition.',
    success: '✅ Composition duplicated',
  },
  list: {
    generic: 'Failed to load compositions.',
    success: '✅ Compositions loaded',
  },
} as const;

/**
 * Create a complete error message for an operation
 *
 * @param operation - Operation type
 * @param error - Error object
 * @returns Complete error message
 */
export function getOperationErrorMessage(
  operation: keyof typeof OPERATION_ERROR_MESSAGES,
  error: unknown
): string {
  const config = getErrorMessage(error);
  const operationMessage = OPERATION_ERROR_MESSAGES[operation].generic;

  return `${config.icon} ${operationMessage} ${config.message} ${config.action || ''}`.trim();
}
